import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import type { EditorActions } from '@atlaskit/editor-core';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss, Box } from '@atlaskit/primitives';

import { SaveIndicatorContext } from './SaveIndicatorContext.tsx';
import { timeFormatter } from './timeFormatter.ts';

const i18n = defineMessages({
	savingMessage: {
		id: 'save-indicator.saving',
		description: 'Save indicator message after user types or edits.',
		defaultMessage: 'Saving',
	},
	allChangesSavedMessage: {
		id: 'save-indicator.all.changes.saved',
		description: 'Save indicator message when user opens an existing page in editor or is idle.',
		defaultMessage: 'Saved',
	},
	savedTooltipMessage: {
		id: 'save-indicator.all.changes.saved.tooltip',
		description: 'Tooltip message of save indicator to indicate all changes are saved.',
		defaultMessage: 'Every change is saved. The last change was {time}.',
	},
	errorTooltipMessage: {
		id: 'save-indicator.error.tooltip',
		description: 'Tooltip message of save indicator to indicate an error.',
		defaultMessage: 'We couldn’t save the latest changes. The last save was {time}.',
	},
});

export type SaveIndicatorComponentProps = {
	editorActions: EditorActions | undefined;
	saveError: boolean;
	title: string | null;
	hasEmoji: boolean;
	hasCoverPicture: boolean;
	selectingEmojiTitle: boolean;
	selectingCoverPicture: boolean;
	formatMessage: any;
};

const saveIndicatorStyles = xcss({
	color: 'color.text.subtle',
	cursor: 'default',
	marginLeft: 'space.050',
	marginRight: 'space.050',
	height: '32px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const saveIndicatorTextStyles = xcss({
	minWidth: '44px',
});

const STATUSES = {
	DISABLED: 'disabled',
	ERROR: 'error',
	SAVING: 'saving',
	SAVED: 'saved',
};

export const SaveIndicatorComponent = ({
	editorActions,
	saveError,
	title,
	hasEmoji,
	hasCoverPicture,
	selectingEmojiTitle,
	selectingCoverPicture,
	formatMessage,
}: SaveIndicatorComponentProps) => {
	const { isSaving, savedTime } = useContext(SaveIndicatorContext);
	const [status, setStatus] = useState<string | null>(null);
	const [tooltip, setTooltip] = useState<string>('');
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const hasSentFeatureExposed = useRef(false);
	const sendFeatureExposed = useCallback(() => {
		if (!hasSentFeatureExposed.current) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					actionSubject: 'feature',
					action: 'exposed',
					source: 'SaveIndicatorTooltip',
				},
			}).fire();
			hasSentFeatureExposed.current = true;
		}
	}, [createAnalyticsEvent]);

	// Update save indicator text
	useEffect(() => {
		if (editorActions?.isDocumentEmpty() && !title && !hasEmoji && !hasCoverPicture) {
			setStatus(STATUSES.DISABLED);
		} else if (saveError) {
			setStatus(STATUSES.ERROR);
		} else if (isSaving || selectingEmojiTitle || selectingCoverPicture) {
			setStatus(STATUSES.SAVING);
		} else {
			setStatus(STATUSES.SAVED);
		}
	}, [
		title,
		hasEmoji,
		hasCoverPicture,
		editorActions,
		saveError,
		isSaving,
		selectingEmojiTitle,
		selectingCoverPicture,
		setStatus,
	]);

	// Update tooltip with last saved timestamp
	const refreshTooltip = useCallback(() => {
		if (status === STATUSES.SAVING) {
			setTooltip('');
		} else if (status === STATUSES.ERROR) {
			sendFeatureExposed();
			setTooltip(
				formatMessage(i18n.errorTooltipMessage, {
					time: timeFormatter(savedTime, formatMessage),
				}),
			);
		} else {
			sendFeatureExposed();
			setTooltip(
				formatMessage(i18n.savedTooltipMessage, {
					time: timeFormatter(savedTime, formatMessage),
				}),
			);
		}
	}, [status, setTooltip, sendFeatureExposed, formatMessage, savedTime]);

	// Responsive save indicator, show icon instead of strings when viewport is small
	const getResponsiveSaveIndicator = useCallback(() => {
		if (status === STATUSES.ERROR) {
			return <StatusWarningIcon spacing="spacious" label="error" />;
		} else if (status === STATUSES.SAVING) {
			return formatMessage(i18n.savingMessage);
		} else if (status === STATUSES.SAVED) {
			return formatMessage(i18n.allChangesSavedMessage);
		}
	}, [status, formatMessage]);

	return (
		<Tooltip
			onShow={refreshTooltip}
			content={tooltip}
			testId="save-indicator-tooltip"
			position="bottom"
		>
			<Box
				testId="save-indicator"
				aria-label={tooltip}
				tabIndex={0}
				role="button"
				paddingInline="space.050"
				xcss={[
					saveIndicatorStyles,
					(status === STATUSES.SAVING || status === STATUSES.SAVED) && saveIndicatorTextStyles,
				]}
			>
				{getResponsiveSaveIndicator()}
			</Box>
		</Tooltip>
	);
};
